// A9 — Verifikatsiya. Module-local data layer for the A9.03..A9.11 sub-flows.
// Follows the `developers/devApi.ts` naming: fail-soft fetchers + module-local
// RU strings (`VERIF_STR`) so this NEVER touches global `src/i18n/*`.
//
// Live endpoints (backend `verification.go`, main.go 858-862):
//   GET  /admin/verification            → { requests: VerifRequest[] }   (A9.01)
//   GET  /admin/verification/:id        → { request: VerifRequest }      (A9.02)
//   POST /admin/verification/:id/decide → { status }   approve|reject    (A9.06)
//   GET  /admin/verified                → { verified: VerifiedRow[] }    (A9.12)
//   POST /admin/verified/:id/revoke     → { ok }                         (A9.08)
// Greenfield (fail-soft to empty / no-op until backend ships): documents,
// domain-checks, external-checks, grant, third-party verifiers, verifier audit.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(
  path: string,
  key: string,
  signal: AbortSignal,
  query?: Record<string, string | number>,
): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal, ...(query ? { query } : {}) })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A9.02/06 — verifikatsiya arizasi (review workbench manbai) ──── */

export interface VerifRequest {
  id: number
  user_id: number
  username?: string | null
  kind: string
  evidence?: string | null
  status: string
  decision_note?: string | null
  created_at: number
  reviewed_at?: number | null
}

/** Pending arizalar (review navbati). Live GET /admin/verification?status=pending. */
export function fetchPendingRequests(signal: AbortSignal): Promise<VerifRequest[]> {
  return safeList<VerifRequest>('/admin/verification', 'requests', signal, {
    status: 'pending',
    limit: 300,
  })
}

/** Bitta ariza (tanlanganda). Live GET /admin/verification/:id. Fail-soft → null. */
export async function fetchRequest(id: number, signal: AbortSignal): Promise<VerifRequest | null> {
  try {
    const data = await api.get<{ request: VerifRequest }>(`/admin/verification/${id}`, { signal })
    return data?.request ?? null
  } catch {
    return null
  }
}

/** A9.06 — qaror. Live POST /admin/verification/:id/decide. Approve => is_official. */
export function decideRequest(id: number, approve: boolean, note: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(`/admin/verification/${id}/decide`, { approve, note })
}

/* ── A9.03 — hujjatlar ──────────────────────────────────────────── */

export interface VerifDoc {
  id: number
  kind: string // passport | license | registration | photo | letter | other
  file_url: string
  mime: string
  uploaded_at: number
  status?: string // pending | ok | invalid
}

/** A9.03 — ariza hujjatlari. Greenfield → fail-soft [] . */
export function fetchDocuments(requestId: number, signal: AbortSignal): Promise<VerifDoc[]> {
  return safeList<VerifDoc>(`/admin/verification/${requestId}/documents`, 'documents', signal)
}

/* ── A9.04 — domen tasdig'i ─────────────────────────────────────── */

export interface DomainCheck {
  id: number
  domain: string
  method: string // dns_txt | meta_tag | file
  token: string
  status: string // verified | pending | failed
  checked_at?: number | null
}

/** A9.04 — domen tekshiruvlari. Greenfield → fail-soft [] . */
export function fetchDomainChecks(requestId: number, signal: AbortSignal): Promise<DomainCheck[]> {
  return safeList<DomainCheck>(`/admin/verification/${requestId}/domain-checks`, 'checks', signal)
}

/** A9.04 — domenni qayta tekshirish. Greenfield → fail-soft no-op. */
export async function runDomainCheck(requestId: number, domain: string): Promise<{ ok: boolean }> {
  try {
    return await api.post<{ ok: boolean }>(`/admin/verification/${requestId}/domain-check`, { domain })
  } catch {
    return { ok: false }
  }
}

/* ── A9.05 — tashqi manba ───────────────────────────────────────── */

export interface ExternalCheck {
  id: number
  source: string // gov_registry | press | social | partner
  reference: string
  result: string // match | no_match | manual | pending
  checked_at?: number | null
}

/** A9.05 — tashqi manba tekshiruvlari. Greenfield → fail-soft [] . */
export function fetchExternalChecks(requestId: number, signal: AbortSignal): Promise<ExternalCheck[]> {
  return safeList<ExternalCheck>(`/admin/verification/${requestId}/external-checks`, 'checks', signal)
}

/** A9.05 — tashqi manbadan qayta so'rov. Greenfield → fail-soft no-op. */
export async function runExternalCheck(requestId: number, source: string): Promise<{ ok: boolean }> {
  try {
    return await api.post<{ ok: boolean }>(`/admin/verification/${requestId}/external-check`, { source })
  } catch {
    return { ok: false }
  }
}

/* ── A9.07/08/12 — reyestr, berish, olib tashlash ───────────────── */

export interface VerifiedRow {
  id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  is_bot: boolean
  created_at: number
}

/** A9.08 manbasi — verifikatsiyalanganlar reyestri. Live GET /admin/verified. */
export function fetchVerifiedRegistry(params: FetchParams): Promise<FetchResult<VerifiedRow>> {
  return safeList<VerifiedRow>('/admin/verified', 'verified', params.signal, { limit: 300 }).then(
    (rows) => toResult(rows, params, (r) => `${r.first_name ?? ''} ${r.username ?? ''} ${r.id}`),
  )
}

/** A9.08 — badge'ni olib tashlash. Live POST /admin/verified/:id/revoke (audited). */
export function revokeVerified(userId: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/verified/${userId}/revoke`, {})
}

/** A9.07 — qo'lda verifikatsiya berish. Greenfield → fail-soft no-op. */
export async function grantVerification(
  userId: number,
  kind: string,
  note: string,
): Promise<{ ok: boolean }> {
  try {
    return await api.post<{ ok: boolean }>('/admin/verified/grant', { user_id: userId, kind, note })
  } catch {
    return { ok: false }
  }
}

/* ── A9.09/10 — uchinchi tomon verifikatorlar ───────────────────── */

export interface VerifierRow {
  id: number
  company: string
  icon_url?: string | null
  domain?: string | null
  status: string // active | suspended
  granted_count: number
  created_at: number
}

/** A9.09 — uchinchi tomon verifikatorlar ro'yxati. Greenfield → fail-soft [] . */
export function fetchVerifiers(params: FetchParams): Promise<FetchResult<VerifierRow>> {
  return safeList<VerifierRow>('/admin/verifiers', 'verifiers', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.company} ${r.domain ?? ''}`),
  )
}

export interface VerifierSettings {
  company: string
  icon_url: string
  domain: string
}

/** A9.10 — verifikator sozlamalari (ikonka, kompaniya). Greenfield → fail-soft. */
export async function saveVerifierSettings(
  id: number,
  patch: VerifierSettings,
): Promise<{ ok: boolean }> {
  try {
    return await api.post<{ ok: boolean }>(`/admin/verifiers/${id}/settings`, patch)
  } catch {
    return { ok: false }
  }
}

/** A9.09 — yangi verifikator qo'shish. Greenfield → fail-soft. */
export async function createVerifier(patch: VerifierSettings): Promise<{ ok: boolean }> {
  try {
    return await api.post<{ ok: boolean }>('/admin/verifiers', patch)
  } catch {
    return { ok: false }
  }
}

/* ── A9.11 — verifikator faoliyati auditi ───────────────────────── */

export interface VerifierAuditRow {
  id: number
  verifier: string
  actor: string
  action: string // grant | revoke | update | login
  target_user?: string | null
  note?: string | null
  created_at: number
}

/** A9.11 — verifikatorlar faoliyati auditi. Greenfield → fail-soft [] . */
export function fetchVerifierAudit(params: FetchParams): Promise<FetchResult<VerifierAuditRow>> {
  return safeList<VerifierAuditRow>('/admin/verifiers/audit', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.verifier} ${r.actor} ${r.action}`),
  )
}

/** Module-local RU strings (global i18n HOT → keyга ko'chiriladi keyinroq). */
export const VERIF_STR = {
  // shared
  back: 'Назад',
  queue: 'Очередь заявок',
  refresh: 'Обновить',
  save: 'Сохранить',
  saved: 'Сохранено',
  failed: 'Не удалось выполнить',
  reason: 'Причина',
  user: 'Пользователь',
  status: 'Статус',
  created: 'Создан',
  kind: 'Тип',
  kindIdentity: 'Личность',
  kindOrg: 'Организация',
  kindNotable: 'Публичная фигура',
  // A9.03..A9.06 review workbench
  review: 'Обзор заявки',
  reviewNote: 'Документы, домен и внешние источники в одном окне',
  pickRequest: 'Выберите заявку слева',
  noPending: 'Нет заявок на рассмотрении',
  evidence: 'Обоснование',
  documents: 'Документы',
  noDocuments: 'Документы не приложены',
  openDoc: 'Открыть',
  domainCheck: 'Проверка домена',
  noDomainChecks: 'Проверок домена нет',
  recheckDomain: 'Проверить домен',
  externalCheck: 'Внешние источники',
  noExternalChecks: 'Внешних проверок нет',
  recheckExternal: 'Запросить проверку',
  decision: 'Решение',
  decisionNote: 'Комментарий к решению',
  approve: 'Подтвердить',
  reject: 'Отклонить',
  approveHint: 'Подтверждение выдаёт значок (is_official) пользователю.',
  approved: 'Заявка подтверждена',
  rejected: 'Заявка отклонена',
  method: 'Метод',
  token: 'Токен',
  source: 'Источник',
  reference: 'Ссылка',
  result: 'Результат',
  checkedAt: 'Проверено',
  // A9.07/08 manage
  manage: 'Значки: выдача и отзыв',
  manageNote: 'Реестр подтверждённых · выдать или отозвать значок',
  grant: 'Выдать значок',
  grantTitle: 'Выдать верификацию',
  grantUserId: 'ID пользователя',
  grantSubmit: 'Выдать',
  granted: 'Значок выдан',
  revoke: 'Отозвать значок',
  revokeTitle: (name: string) => `Отозвать значок у ${name}?`,
  revokeDesc: 'Пользователь потеряет значок верификации (is_official). Действие записывается в аудит.',
  revoked: 'Значок отозван',
  username: 'Юзернейм',
  registered: 'Регистрация',
  // A9.09/10 verifiers
  verifiers: 'Сторонние верификаторы',
  verifiersNote: 'Организации-верификаторы, их иконки и настройки',
  addVerifier: 'Добавить верификатора',
  company: 'Компания',
  icon: 'Иконка',
  iconUrl: 'URL иконки',
  domain: 'Домен',
  grantedCount: 'Выдано значков',
  settings: 'Настройки',
  settingsTitle: 'Настройки верификатора',
  created2: 'Добавлен',
  active: 'Активен',
  suspended: 'Приостановлен',
  // A9.11 audit
  audit: 'Аудит верификаторов',
  auditNote: 'Действия сторонних верификаторов',
  actor: 'Исполнитель',
  action: 'Действие',
  targetUser: 'Пользователь',
  note: 'Заметка',
  actGrant: 'Выдача',
  actRevoke: 'Отзыв',
  actUpdate: 'Изменение',
  actLogin: 'Вход',
} as const
